import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal, ChapterNumber } from "../components/motion";
import { MarqueeBand } from "../components/MarqueeBand";
import { usePageTitle } from "../lib/title";
import headshot from "../assets/kyle-walker-headshot.jpg";
const values = [
  {
    n: "01",
    title: "Clarity over complexity",
    body: "Insurance thrives on confusion. I exist to end it — translating every plan, term, and trade-off into language a busy person can act on.",
  },
  {
    n: "02",
    title: "Clients for seasons, not transactions",
    body: "A harvest is never one day's work. I build relationships meant to last through new jobs, new babies, new businesses, and every renewal in between — clients who know their success is the only driver of my legacy.",
  },
  {
    n: "03",
    title: "Independence you can verify",
    body: "I'm not captive to a single carrier. My recommendations follow your doctors, your budget, and your worst-case math — nothing else.",
  },
  {
    n: "04",
    title: "Education as the product",
    body: "The plan matters, but the understanding matters more. Every client should walk away knowing more about their benefits than when they arrived.",
  },
];

export default function About() {
  usePageTitle("About Kyle Walker — The Woodlands, TX");
  return (
    <div data-testid="about-page">
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-36 lg:px-10 lg:pt-44" data-testid="about-hero">
        <h1 className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="about-kicker">
          Kyle Walker · Licensed Texas Health Insurance Agent
        </h1>
        <MaskedLines
          as="p"
          className="mt-8 max-w-4xl font-serif text-5xl font-light leading-[0.95] tracking-tighter text-forest sm:text-6xl lg:text-7xl"
          lines={["Planted in Texas.", "Grown for people."]}
        />
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-6 pb-24 lg:grid-cols-12 lg:px-10 lg:pb-32">
        <Reveal className="lg:col-span-7">
          <div className="space-y-6 text-base leading-relaxed text-ink/70 sm:text-lg" data-testid="about-story">
            <p>
              OneHarvestCo. Benefits began with a simple observation: most people don't dislike their health
              insurance because of what it covers — they dislike it because no one ever explained it.
            </p>
            <p>
              After becoming a licensed Texas health insurance agent, I set out to build a different
              kind of practice — one where education comes before enrollment, where individuals and employers
              get the same patient attention, and where success is measured in understanding, not just policies
              written.
            </p>
            <p>
              The name says it plainly. A harvest is the reward of patient, careful cultivation. Benefits work
              the same way: designed thoughtfully, explained honestly, and tended year after year, they provide
              for people exactly when they need it most.
            </p>
            <p>
              The vision behind it all is simple and stubborn: trusted, long-term relationships through every
              season of life and business. Not a quick dollar — a lasting, beautiful legacy, built with clients
              who know unequivocally that their success is the only driver of mine.
            </p>
          </div>
          <Reveal delay={0.1}>
            <blockquote
              className="mt-12 border-l-2 border-gold pl-8 font-serif text-2xl font-light italic leading-snug text-forest sm:text-3xl"
              data-testid="about-quote"
            >
              &ldquo;Help people understand their benefits, and the right decisions make themselves.&rdquo;
            </blockquote>
          </Reveal>
        </Reveal>
      </section>

      <MarqueeBand items={["Understand", "Optimize", "Provide", "Protect"]} dark />

      {/* FOUNDER */}
      <section className="bg-sand/70" data-testid="founder-section">
        <div className="mx-auto max-w-4xl px-6 py-24 lg:px-10 lg:py-32">
          <div>
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="founder-kicker">
                Meet the Founder
              </p>
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
                Hi, I&rsquo;m <em className="text-terra">Kyle Walker</em>.
              </h2>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/40" data-testid="founder-caption">
                Founder &amp; Licensed Texas Agent · Born &amp; Raised in The Woodlands
              </p>
              <div className="mt-8 space-y-5 text-base leading-relaxed text-ink/70" data-testid="founder-bio">
                <p>
                  I&rsquo;m a licensed Texas health insurance agent, and I started OneHarvestCo. Benefits with a
                  conviction I couldn&rsquo;t shake: people don&rsquo;t need more insurance jargon — they need someone
                  in their corner who speaks plainly.
                </p>
                <p>
                  When you work with OneHarvestCo. Benefits, you work with me. I&rsquo;ll learn your doctors, your budget, and
                  your worries — then compare real options side by side and walk you through the trade-offs until
                  the right choice feels obvious. No pressure, no quotas, no clock watching.
                </p>
                <p>
                  Above everything else, I&rsquo;m a husband and a father — my family is my top priority and my
                  daily reminder of why this work matters. Behind every policy is a person, and often the people
                  they love, counting on that coverage to be there when it counts. That&rsquo;s the standard I hold
                  every recommendation to: would I put my own family in this plan?
                </p>
                <p>
                  Those roots run deep. I was born and raised in The Woodlands — played baseball for the
                  Highlanders at The Woodlands High School, went on to play at the University of Texas, and
                  continued my career in the Colorado Rockies organization. Today, my wife and I are raising our
                  three kids right here, in the place we&rsquo;ve always loved and always considered home. This
                  community and our neighbors are a big part of why OneHarvestCo. exists — serving them well is
                  personal.
                </p>
                <p>
                  The market has also changed more in the last few years than in the two decades before it. There are newer ways to get and pay for care — direct primary care, virtual-first plans, level-funded and reference-based pricing, and CHOICE arrangements (formerly ICHRA) — and each one comes with its own trade-offs. Most people never hear about any of it,
                  because the familiar channels keep showing the same few options, so I make sure you see the full
                  picture before you choose.
                </p>
                <p>
                  My goal isn&rsquo;t a signature on a policy. It&rsquo;s a relationship that lasts through every
                  season of your life or your business — and a Texas where people actually understand the benefits
                  they&rsquo;re paying for.
                </p>
              </div>
              <div className="mt-8 flex items-center gap-5" data-testid="founder-signoff">
                <img
                  src={headshot}
                  alt="Kyle Walker, Founder, OHC Benefits"
                  loading="lazy"
                  width="320"
                  height="320"
                  data-testid="founder-headshot"
                  className="h-20 w-20 rounded-xl border border-border object-cover shadow-[0_8px_24px_-12px_rgba(26,54,38,0.45)] lg:h-28 lg:w-28"
                />
                <p className="font-serif text-3xl font-light italic text-forest" data-testid="founder-signature">
                  — Kyle Walker
                </p>
              </div>
              <a
                href="mailto:kyle@ohcbenefits.com"
                data-testid="founder-email-cta"
                className="group mt-8 inline-flex items-center gap-2 rounded-full border border-forest/30 px-7 py-3 text-sm font-medium text-forest transition-all duration-300 hover:-translate-y-0.5 hover:border-gold hover:text-terra"
              >
                Say Hello — kyle@ohcbenefits.com
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section className="border-t border-border" data-testid="about-experience-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="experience-kicker">
              The Experience Behind the Advice
            </p>
            <h2 className="mt-6 max-w-4xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl lg:text-6xl">
              15+ years inside healthcare — <em className="text-terra">before a single plan was sold</em>.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-6">
              <div className="space-y-6 text-base leading-relaxed text-ink/70 sm:text-lg" data-testid="experience-narrative">
                <p>
                  Long before OneHarvestCo. Benefits, I spent more than fifteen years working in healthcare
                  delivery and care models — the side of the system where care actually happens. That&rsquo;s where I learned how care is actually delivered, coordinated and paid for, and how much the details matter once you&rsquo;re the one using a plan.
                </p>
                <p>
                  I was directly involved in the build-out and development of direct care and direct contracting arrangements — including Direct Primary Care (DPC) and concierge models — so I know how they work from the inside.
                </p>
                <p>
                  That inside view shapes every recommendation I make: a plan isn&rsquo;t &ldquo;good&rdquo; in the abstract. It has to fit you — your doctors, your health, your life. That kind of innovation and forward thinking is what delivers
                  real value when you&rsquo;re deciding on a benefits program.
                </p>
                <p>
                  Today, my employer-facing work is purely independent — I help companies understand and
                  analyze their PEO and benefits arrangements with the same data-first rigor, and with zero
                  conflict: if you already have the right structure, the analysis will say so.
                </p>
              </div>
            </Reveal>
            <div className="lg:col-span-5 lg:col-start-8">
              {[
                ["Care Delivery & Care Models", "Fifteen-plus years inside the delivery system — understanding how care is actually provided, coordinated, and paid for."],
                ["DPC & Concierge Care", "Hands-on involvement building direct care and direct contracting arrangements, including DPC and concierge models."],
                ["Client-First Plan Review", "Every option checked against your doctors and your needs — forward-thinking structure applied to your benefits program."],
              ].map(([title, body], i) => (
                <Reveal key={title} delay={0.07 * i}>
                  <div
                    className={`border-t border-border py-7 ${i === 2 ? "border-b" : ""}`}
                    data-testid={`experience-pillar-${i + 1}`}
                  >
                    <span className="font-mono text-xs tracking-[0.35em] text-terra">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="mt-3 font-serif text-2xl font-medium tracking-tight text-forest">{title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-ink/65">{body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="about-values">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra">What I Stand On</p>
          <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
            Four roots of the <em className="text-terra">company</em>
          </h2>
        </Reveal>
        <div className="mt-14">
          {values.map((v, i) => (
            <Reveal key={v.n} delay={0.05 * i}>
              <div
                className={`grid gap-6 border-t border-border py-12 lg:grid-cols-12 lg:py-16 ${
                  i === values.length - 1 ? "border-b" : ""
                }`}
                data-testid={`about-value-${v.n}`}
              >
                <div className="lg:col-span-2">
                  <ChapterNumber n={v.n} />
                </div>
                <div className="lg:col-span-4">
                  <h3 className="font-serif text-2xl font-medium tracking-tight text-forest sm:text-3xl">{v.title}</h3>
                </div>
                <div className="flex items-end lg:col-span-6">
                  <p className="text-base leading-relaxed text-ink/70">{v.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <Link
            to="/contact?type=consultation"
            data-testid="about-consultation-cta"
            className="group mt-14 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-moss"
          >
            Start a Conversation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
